import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, BarChart3, Clock, Users, 
  DollarSign, Loader2, Lightbulb, AlertTriangle, Info
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useLanguage } from '../context/LanguageContext';

import { API_URL } from '../utils/apiConfig';

export default function MerchantAnalytics({ token, isOpen, onClose }) {
  const [overview, setOverview] = useState(null);
  const [dailySales, setDailySales] = useState([]);
  const [hourlyDist, setHourlyDist] = useState([]);
  const [topBeneficiaries, setTopBeneficiaries] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  
  const { language } = useLanguage();
  
  const t = {
    en: {
      title: 'Sales Analytics',
      period: 'Period',
      days7: 'Last 7 days',
      days30: 'Last 30 days',
      days60: 'Last 60 days',
      days90: 'Last 90 days',
      overview: 'Overview',
      totalRedemptions: 'Total Redemptions',
      totalRevenue: 'Total Revenue',
      avgRedemption: 'Avg Redemption',
      growth: 'Growth',
      vsLastPeriod: 'vs last period',
      activeAuths: 'Active Authorizations',
      pendingDOP: 'Pending Redemption',
      dailyTrend: 'Daily Trend',
      peakHours: 'Peak Hours',
      topCustomers: 'Top Customers',
      visits: 'visits',
      lastVisit: 'Last visit',
      insights: 'Insights & Tips',
      noData: 'No data available for this period'
    },
    es: {
      title: 'Análisis de Ventas',
      period: 'Período',
      days7: 'Últimos 7 días',
      days30: 'Últimos 30 días',
      days60: 'Últimos 60 días',
      days90: 'Últimos 90 días',
      overview: 'Resumen',
      totalRedemptions: 'Canjes Totales',
      totalRevenue: 'Ingresos Totales',
      avgRedemption: 'Canje Promedio',
      growth: 'Crecimiento',
      vsLastPeriod: 'vs período anterior',
      activeAuths: 'Autorizaciones Activas',
      pendingDOP: 'Pendiente de Canjear',
      dailyTrend: 'Tendencia Diaria',
      peakHours: 'Horas Pico',
      topCustomers: 'Mejores Clientes',
      visits: 'visitas',
      lastVisit: 'Última visita',
      insights: 'Consejos y Recomendaciones',
      noData: 'Sin datos para este período'
    }
  };
  
  const text = t[language] || t.en;
  
  useEffect(() => {
    if (isOpen && token) {
      fetchAnalytics();
    }
  }, [isOpen, token, period]);
  
  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const days = parseInt(period);
      
      const [overviewRes, dailyRes, hourlyRes, topRes, insightsRes] = await Promise.all([
        fetch(`${API_URL}/api/merchant/analytics/overview?days=${days}`, { headers }),
        fetch(`${API_URL}/api/merchant/analytics/daily-sales?days=${days}`, { headers }),
        fetch(`${API_URL}/api/merchant/analytics/hourly-distribution?days=${days}`, { headers }),
        fetch(`${API_URL}/api/merchant/analytics/top-beneficiaries?days=${days}&limit=5`, { headers }),
        fetch(`${API_URL}/api/merchant/analytics/insights`, { headers })
      ]);
      
      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (dailyRes.ok) {
        const data = await dailyRes.json();
        setDailySales(data.daily_sales || []);
      }
      if (hourlyRes.ok) {
        const data = await hourlyRes.json();
        setHourlyDist(data.hourly_distribution || []);
      }
      if (topRes.ok) {
        const data = await topRes.json();
        setTopBeneficiaries(data.top_beneficiaries || []);
      }
      if (insightsRes.ok) {
        const data = await insightsRes.json();
        setInsights(data.insights || []);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  // Calculate max for chart scaling
  const maxDailyDOP = Math.max(...dailySales.map(d => d.total_dop), 1);
  const maxHourlyCount = Math.max(...hourlyDist.map(h => h.count), 1);
  
  const getInsightIcon = (type) => {
    switch (type) {
      case 'positive': return <TrendingUp className="w-5 h-5 text-emerald-600" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      case 'tip': return <Lightbulb className="w-5 h-5 text-blue-600" />;
      default: return <Info className="w-5 h-5 text-slate-600" />;
    }
  };
  
  const getInsightBg = (type) => {
    switch (type) {
      case 'positive': return 'bg-emerald-50 border-emerald-200';
      case 'warning': return 'bg-amber-50 border-amber-200';
      case 'tip': return 'bg-blue-50 border-blue-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">{text.title}</h2>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="analytics-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">{text.days7}</SelectItem>
                <SelectItem value="30">{text.days30}</SelectItem>
                <SelectItem value="60">{text.days60}</SelectItem>
                <SelectItem value="90">{text.days90}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={onClose}>×</Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overview Stats */}
              {overview && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <BarChart3 className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500">{text.totalRedemptions}</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{overview.redemptions?.total || 0}</p>
                    {overview.redemptions?.growth_rate_percent !== 0 && (
                      <div className={`flex items-center gap-1 mt-1 text-xs ${overview.redemptions?.growth_rate_percent > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {overview.redemptions?.growth_rate_percent > 0 ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {Math.abs(overview.redemptions?.growth_rate_percent || 0)}% {text.vsLastPeriod}
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500">{text.totalRevenue}</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600">
                      RD${(overview.redemptions?.total_dop || 0).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500">{text.avgRedemption}</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">
                      RD${(overview.redemptions?.average_dop || 0).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500">{text.pendingDOP}</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">
                      RD${(overview.authorizations?.pending_redemption_dop || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {overview.authorizations?.currently_active || 0} {text.activeAuths}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Daily Trend Chart */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h3 className="font-semibold text-slate-900 mb-4">{text.dailyTrend}</h3>
                {dailySales.length > 0 ? (
                  <div className="h-40 flex items-end gap-1">
                    {dailySales.slice(-14).map((day, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                          style={{ height: `${Math.max((day.total_dop / maxDailyDOP) * 100, 2)}%` }}
                          title={`${day.date}: RD$${day.total_dop.toLocaleString()}`}
                        />
                        {i % 2 === 0 && (
                          <span className="text-[10px] text-slate-400 mt-1 rotate-45 origin-left">
                            {day.date.slice(5)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">{text.noData}</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Peak Hours */}
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-900 mb-4">{text.peakHours}</h3>
                  {hourlyDist.length > 0 ? (
                    <div className="h-32 flex items-end gap-0.5">
                      {hourlyDist.map((hour, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center">
                          <div 
                            className={`w-full rounded-t transition-all ${hour.count > 0 ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-slate-100'}`}
                            style={{ height: `${Math.max((hour.count / maxHourlyCount) * 100, 2)}%` }}
                            title={`${hour.hour}:00 - ${hour.count} redemptions`}
                          />
                          {i % 4 === 0 && (
                            <span className="text-[10px] text-slate-400 mt-1">{hour.hour}h</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">{text.noData}</p>
                  )}
                </div>
                
                {/* Top Customers */}
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-900 mb-4">{text.topCustomers}</h3>
                  {topBeneficiaries.length > 0 ? (
                    <div className="space-y-3">
                      {topBeneficiaries.map((b, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                              <Users className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{b.name}</p>
                              <p className="text-xs text-slate-500">{b.visit_count} {text.visits}</p>
                            </div>
                          </div>
                          <span className="font-bold text-emerald-600">RD${b.total_dop.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">{text.noData}</p>
                  )}
                </div>
              </div>
              
              {/* Insights */}
              {insights.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">{text.insights}</h3>
                  <div className="space-y-3">
                    {insights.map((insight, i) => (
                      <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border ${getInsightBg(insight.type)}`}>
                        {getInsightIcon(insight.type)}
                        <div>
                          <p className="font-medium text-slate-900">{insight.title}</p>
                          <p className="text-sm text-slate-600">{insight.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
